import { auth, storage } from '../firebase/config';

/**
 * Firebase Storage boundary — the ONE place the app reaches the Storage SDK from the presentation layer.
 * Components/hooks must go through here (the layer rule: UI → service → SDK), never import `firebase/storage`
 * directly. The SDK is lazy-imported inside each call so the (heavy) storage bundle stays code-split, exactly
 * as the callers did inline before.
 */

/** Resolve a Storage object path to a short-lived download URL. */
export async function resolveDownloadUrl(path: string): Promise<string> {
  const { ref, getDownloadURL } = await import('firebase/storage');
  return getDownloadURL(ref(storage, path));
}

/** Upload bytes/blob/file to a Storage path (resumable, awaited to completion). */
export async function uploadToPath(
  path: string,
  data: Blob | Uint8Array | ArrayBuffer | File,
  contentType?: string,
): Promise<void> {
  const { ref, uploadBytesResumable } = await import('firebase/storage');
  await uploadBytesResumable(ref(storage, path), data, contentType ? { contentType } : undefined);
}

/**
 * Upload into the SIGNED-IN USER'S OWN uploads (`chat-attachments/<uid>/<folder>/...`) and return the path.
 *
 * Storage rules only let a client write under its own uid (storage.rules, chat-attachments), and the server only
 * accepts a reference photo from that prefix. Callers used to build the path themselves, and the Studio's cast,
 * location and object uploads put the word `studio` where the uid belongs, so every one was denied (BUGS.md
 * 2026-09-27). Resolving the uid here, at upload time, makes that mistake impossible for any caller.
 */
export async function uploadToOwnFolder(folder: string, fileName: string, data: Blob | File, contentType?: string): Promise<string> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not signed in');
  const safeFolder = folder.replace(/^\/+|\/+$/g, '');
  const path = `chat-attachments/${uid}/${safeFolder}/${fileName}`;
  await uploadToPath(path, data, contentType);
  return path;
}

/** Fetch a URL into a Blob (for download-to-device / offline playback of a shared asset). */
export async function fetchAsBlob(url: string): Promise<Blob> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch asset (${res.status})`);
  return res.blob();
}

/**
 * Recover the object PATH from one of OUR tokenless public download URLs, or null for anything else.
 *
 * Gallery posts are normalized server-side to `firebasestorage.googleapis.com/v0/b/<bucket>/o/<path>?alt=media`
 * WITHOUT a token — but `agent-gallery/` reads require a signed-in user (storage.rules), and a bare <img>
 * carries no auth, so those URLs 403 for everyone (screenshot78). Extracting the path lets the client
 * re-resolve through the web SDK, which authenticates and returns a token URL the <img> can actually load.
 * External URLs (or data:) return null and are used as-is.
 */
export const pathFromPublicUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  const m = /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)/.exec(url);
  if (!m) return null;
  if (/[?&]token=/.test(url)) return null;   // a tokened URL already works — never degrade it
  try { return decodeURIComponent(m[1]); } catch { return null; }
};
